// 출퇴근 정보 — 관리자 수기 입력·수정.
//  - ManualEntryModal: 카드를 안 찍은 날 출근/퇴근 시각을 새로 넣는다 (직원 · 날짜 · 출근 · 퇴근 · 사유)
//  - DayEditModal: 한 사람의 하루 기록(카드·수기)을 시각 수정 · 삭제 · 추가하고 수정 이력을 본다
// 권한: 출퇴근관리(commute) 추가=can_create, 수정=can_update, 삭제=can_delete (서버도 같은 기준). 모든 변경은 사유 필수.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, Form, Select, DatePicker, TimePicker, Input, Button, Space, Table, Tag, Typography, Popconfirm,
  Alert, Grid, message,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

export const EVENT_LABEL = { IN: '출근', OUT: '퇴근' };
const ACTION_LABEL = { CREATE: '추가', UPDATE: '수정', DELETE: '삭제' };
const ACTION_COLOR = { CREATE: 'green', UPDATE: 'blue', DELETE: 'red' };

const errMsg = (e, fallback) => e.response?.data?.message || fallback;

const useIsMobile = () => {
  const screens = Grid.useBreakpoint();
  return screens.md === undefined ? window.innerWidth < 768 : !screens.md;
};

/* ── 수기 입력 (새 날짜) ─────────────────────────── */
export const ManualEntryModal = ({ api, open, onClose, onSaved, initial }) => {
  const [form] = Form.useForm();
  const [employees, setEmployees] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();

  const search = useCallback(async (q) => {
    setSearching(true);
    try {
      const { data } = await api.get('/api/attendance/manual/employees', { params: { q } });
      setEmployees(data.data || []);
    } catch (e) {
      message.error(errMsg(e, '직원 목록을 불러오지 못했습니다.'));
    } finally {
      setSearching(false);
    }
  }, [api]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      employee_id: initial?.employee_id,
      date: initial?.date ? dayjs(initial.date) : dayjs(),
      in_time: null,
      out_time: null,
    });
    search('');
  }, [open, initial, form, search]);

  const submit = async (v) => {
    if (!v.in_time && !v.out_time) { message.warning('출근 또는 퇴근 시각을 하나 이상 입력하세요.'); return; }
    if (v.in_time && v.out_time && !v.out_time.isAfter(v.in_time)) { message.warning('퇴근 시각이 출근보다 늦어야 합니다.'); return; }
    const date = v.date.format('YYYY-MM-DD');
    const items = [
      v.in_time && { event_type: 'IN', event_time: `${date} ${v.in_time.format('HH:mm')}` },
      v.out_time && { event_type: 'OUT', event_time: `${date} ${v.out_time.format('HH:mm')}` },
    ].filter(Boolean);
    setSaving(true);
    try {
      for (const it of items) {
        // eslint-disable-next-line no-await-in-loop
        await api.post('/api/attendance/manual', { employee_id: v.employee_id, reason: v.reason.trim(), ...it });
      }
      message.success('출퇴근 기록을 추가했습니다.');
      onSaved?.();
      onClose();
    } catch (e) {
      message.error(errMsg(e, '저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="출퇴근 수기 입력" open={open} onCancel={onClose} destroyOnClose maskClosable={false}
      width={isMobile ? '100%' : 480} style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : undefined}
      footer={[
        <Button key="c" onClick={onClose}>취소</Button>,
        <Button key="s" type="primary" loading={saving} onClick={() => form.submit()}>저장</Button>,
      ]}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        카드를 찍지 못한 날의 출근·퇴근 시각을 입력합니다. 한쪽만 넣어도 됩니다.
      </Text>
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
        <Form.Item name="employee_id" label="직원" rules={[{ required: true, message: '직원을 선택하세요.' }]}>
          <Select
            showSearch size="large" placeholder="이름 검색" filterOption={false} loading={searching}
            onSearch={search} notFoundContent={searching ? '검색 중...' : '없음'}
            options={employees.map((e) => ({
              value: e.id,
              label: `${e.name}${e.department ? ` · ${e.department}` : ''}${e.employment_type === 'OUTSOURCING' ? ` · ${e.vendor_name || '아웃소싱'}` : ''}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="date" label="날짜" rules={[{ required: true, message: '날짜를 선택하세요.' }]}>
          <DatePicker size="large" style={{ width: '100%' }} format="YYYY-MM-DD (dd)" allowClear={false} inputReadOnly={isMobile} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="in_time" label="출근" style={{ flex: 1, marginRight: 8 }}>
            <TimePicker size="large" format="HH:mm" minuteStep={5} style={{ width: '100%' }} placeholder="예) 08:30" inputReadOnly={isMobile} needConfirm={false} />
          </Form.Item>
          <Form.Item name="out_time" label="퇴근" style={{ flex: 1 }}>
            <TimePicker size="large" format="HH:mm" minuteStep={5} style={{ width: '100%' }} placeholder="예) 17:30" inputReadOnly={isMobile} needConfirm={false} />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="reason" label="사유" rules={[{ required: true, whitespace: true, message: '사유를 입력하세요.' }]}>
          <Input size="large" maxLength={255} placeholder="예) 카드 미지참, 단말기 고장" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ── 하루 기록 수정 ─────────────────────────────── */
export const DayEditModal = ({ api, open, onClose, onSaved, target, perms }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState({ type: 'IN', time: null });
  const [adding, setAdding] = useState({ type: 'OUT', time: null });
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    try {
      const { data: res } = await api.get('/api/attendance/manual/day', { params: { employee_id: target.employee_id, date: target.date } });
      setData(res);
    } catch (e) {
      message.error(errMsg(e, '기록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [api, target]);

  useEffect(() => {
    if (!open) return;
    setData(null); setReason(''); setEditId(null); setChanged(false);
    setAdding({ type: 'OUT', time: null });
    load();
  }, [open, load]);

  const needReason = () => {
    if (reason.trim()) return false;
    message.warning('위의 사유를 먼저 입력하세요.');
    return true;
  };

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      message.success(okText);
      setChanged(true);
      setEditId(null);
      await load();
    } catch (e) {
      message.error(errMsg(e, '저장에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = (log) => {
    if (needReason() || !editVal.time) return;
    run(() => api.put(`/api/attendance/manual/${log.id}`, {
      event_type: editVal.type, event_time: `${target.date} ${editVal.time.format('HH:mm')}`, reason: reason.trim(),
    }), '수정했습니다.');
  };

  const remove = (log) => {
    if (needReason()) return;
    run(() => api.delete(`/api/attendance/manual/${log.id}`, { data: { reason: reason.trim() } }), '삭제했습니다.');
  };

  const add = () => {
    if (needReason()) return;
    if (!adding.time) { message.warning('시각을 선택하세요.'); return; }
    run(() => api.post('/api/attendance/manual', {
      employee_id: target.employee_id, event_type: adding.type,
      event_time: `${target.date} ${adding.time.format('HH:mm')}`, reason: reason.trim(),
    }), '추가했습니다.');
  };

  const close = () => { if (changed) onSaved?.(); onClose(); };

  const typeSelect = (value, onChange) => (
    <Select value={value} onChange={onChange} style={{ width: 80 }} options={[{ value: 'IN', label: '출근' }, { value: 'OUT', label: '퇴근' }]} />
  );
  const timePicker = (value, onChange) => (
    <TimePicker value={value} onChange={onChange} format="HH:mm" minuteStep={1} style={{ width: 100 }} inputReadOnly={isMobile} needConfirm={false} allowClear={false} />
  );

  const srcTags = (r) => (
    <Space size={2} wrap>
      {r.is_manual ? <Tag color="orange">수기</Tag> : <Tag>카드</Tag>}
      {r.is_edited && <Tag color="gold">수정됨</Tag>}
    </Space>
  );

  const columns = [
    {
      title: '구분', dataIndex: 'event_type', width: isMobile ? 70 : 90,
      render: (v, r) => (editId === r.id
        ? typeSelect(editVal.type, (type) => setEditVal((s) => ({ ...s, type })))
        : <Tag color={v === 'IN' ? 'blue' : 'purple'}>{EVENT_LABEL[v]}</Tag>),
    },
    {
      title: '시각', dataIndex: 'event_time', width: isMobile ? undefined : 110,
      render: (v, r) => (editId === r.id
        ? timePicker(editVal.time, (time) => setEditVal((s) => ({ ...s, time })))
        : (
          <Space size={4} wrap>
            <Text strong>{String(v).slice(11, 16)}</Text>
            {isMobile && srcTags(r)}
          </Space>
        )),
    },
    // 폰에서는 출처 태그를 시각 옆에 붙여 수정·삭제 버튼이 잘리지 않게
    ...(isMobile ? [] : [{ title: '출처', key: 'src', render: (_, r) => srcTags(r) }]),
    {
      title: '', key: 'act', align: 'right', width: 80,
      render: (_, r) => (editId === r.id ? (
        <Space size={4}>
          <Button size="small" type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => saveEdit(r)} aria-label="저장" />
          <Button size="small" icon={<CloseOutlined />} onClick={() => setEditId(null)} aria-label="취소" />
        </Space>
      ) : (
        <Space size={4}>
          {perms.update && (
            <Button size="small" icon={<EditOutlined />} aria-label="수정"
              onClick={() => { setEditId(r.id); setEditVal({ type: r.event_type, time: dayjs(r.event_time) }); }} />
          )}
          {perms.delete && (
            <Popconfirm title="이 기록을 삭제할까요?" okText="삭제" okButtonProps={{ danger: true }} cancelText="취소" onConfirm={() => remove(r)}>
              <Button size="small" danger icon={<DeleteOutlined />} aria-label="삭제" />
            </Popconfirm>
          )}
        </Space>
      )),
    },
  ];

  const emp = data?.employee;

  return (
    <Modal
      open={open} onCancel={close} destroyOnClose maskClosable={false}
      title={target ? `${emp?.name || target.name || ''} · ${dayjs(target.date).format('YYYY-MM-DD (dd)')} 출퇴근 수정` : ''}
      width={isMobile ? '100%' : 620} style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } : undefined}
      footer={<Button onClick={close}>닫기</Button>}
    >
      <Input
        size="large" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255}
        addonBefore="사유" placeholder="예) 카드 미지참, 퇴근 시 미태그" style={{ marginBottom: 12 }}
      />
      <Table
        rowKey="id" size="small" loading={loading} pagination={false} columns={columns}
        dataSource={data?.logs || []} locale={{ emptyText: '이날 기록이 없습니다.' }}
      />
      {perms.create && (
        <Space wrap style={{ marginTop: 12 }}>
          {typeSelect(adding.type, (type) => setAdding((s) => ({ ...s, type })))}
          {timePicker(adding.time, (time) => setAdding((s) => ({ ...s, time })))}
          <Button icon={<PlusOutlined />} loading={busy} onClick={add}>기록 추가</Button>
        </Space>
      )}
      <Alert
        type="info" showIcon style={{ marginTop: 12 }}
        message="하루 출근은 가장 이른 '출근', 퇴근은 가장 늦은 '퇴근' 기록으로 계산됩니다."
      />
      {data?.history?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text strong>수정 이력</Text>
          <ul className="att-edit-history">
            {data.history.map((h) => (
              <li key={h.id}>
                <Tag color={ACTION_COLOR[h.action]}>{ACTION_LABEL[h.action]}</Tag>
                <span>
                  {h.before_event_type && `${EVENT_LABEL[h.before_event_type]} ${h.before_event_time.slice(11)}`}
                  {h.before_event_type && h.after_event_type && ' → '}
                  {h.after_event_type && `${EVENT_LABEL[h.after_event_type]} ${h.after_event_time.slice(11)}`}
                </span>
                <Text type="secondary"> · {h.reason} · {h.edited_by} · {h.edited_at}</Text>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
};
